/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { Alert, Box, Button, Divider, Link, Snackbar } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { DragEvent, Fragment, ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import ComponentTemplateSelector from './ComponentTemplateSelector'
import PreviewComponent from './PreviewComponent'
import RequestHandler from '../../../../lib/request_handler'
import useDebounce from '../../../../lib/useDebounce'
import { FeaturedArticle, FeaturedArticleComponentPropTemplate } from '../../../types'
import routes from '../../../utils/routes'

type Props = {
  article: FeaturedArticle
  componentTemplates: FeaturedArticleComponentPropTemplate[]
}

type FormProps = {
  body: string | null
}

type SnackBarState = {
  open: boolean
  message: string
  severity: 'success' | 'error'
}

function Show(props: Props): ReactElement {
  const article = props.article
  const componentTemplates = props.componentTemplates
  const [snackbarState, setSnackbarState] = useState<SnackBarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  const { register, handleSubmit, watch, setValue } = useForm<FormProps>({
    shouldUnregister: false,
    defaultValues: {
      body: article.content.body || '',
    },
  })

  // bodyへのrefをreact-hook-formとref双方で持っておきたい
  // refをドラッグ・アンド・ドロップで用いるため
  const { ref, ...restBodyRegister } = register('body')
  const bodyRef = useRef(null)

  const watchBody = watch('body')
  const debouncedBody = useDebounce<string>(watchBody, 500)

  const onSubmit = async (data: FormProps) => {
    const handler = new RequestHandler(
      routes.staff_admin_featured_article_content_path(article.id),
      'PUT',
      data,
      true
    )
    try {
      await handler.requestWithBody()
      setSnackbarState({ message: '保存しました', open: true, severity: 'success' })
    } catch {
      setSnackbarState({ message: '保存に失敗しました', open: true, severity: 'error' })
    }
  }

  const splitBodyTextWithCursor = (): [string, string] => {
    const textarea: HTMLTextAreaElement = bodyRef.current as HTMLTextAreaElement
    const beforeString: string = watchBody.substr(0, textarea.selectionStart)
    const afterString: string = watchBody.substr(textarea.selectionStart)
    return [beforeString, afterString]
  }

  const insertTextWithCursor = (text: string) => {
    // NOTE: document.execCommand('insertText', false, text) を使わないとundo/redoできないが、execCommandがobsoleteで代替がないため
    // 現状では現行の実装以外の手段はない
    const [beforeString, afterString] = splitBodyTextWithCursor()
    setValue('body', [beforeString, text, afterString].join(''))
  }

  const handleBodyDrop = async (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // ドラッグ・アンド・ドロップされたファイルを取得
    const items = e.dataTransfer.items
    const files: File[] = []
    for (let i = 0; i < items.length; i++) {
      files.push(items[i].getAsFile())
    }

    // loading textをテキストエリアに表示する
    const [beforeString, afterString] = splitBodyTextWithCursor()
    setValue('body', [beforeString, '画像コンポーネントをアップロード中……', afterString].join(''))

    // ファイルをアップロードし、bargain image id を取得する
    const uploadFiles = async (files: File[]): Promise<number[]> => {
      const imageIds: number[] = []

      try {
        for (const file of files) {
          const handler = new RequestHandler(
            routes.staff_admin_images_path(),
            'POST',
            { image: file },
            true
          )
          const response = (await handler.requestWithBody()) as { body: { id: number } }
          imageIds.push(response.body.id)
        }
      } catch {
        setSnackbarState({ message: 'アップロードに失敗しました', open: true, severity: 'error' })
        return []
      }

      return imageIds
    }

    const imageIds: number[] = await uploadFiles(files)

    // loading textを削除して画像コンポーネントを入稿する
    const createImageComponentString = (imageId: number): string =>
      `<tk:image image_id="${imageId}" />`
    const imageComponentString: string = imageIds
      .map((imageId) => createImageComponentString(imageId))
      .join('\n')

    setValue('body', [beforeString, imageComponentString, afterString].join(''))
  }

  const onPreviewError = useCallback(() => {
    setSnackbarState({
      message: 'プレビューの描画に失敗しました',
      open: true,
      severity: 'error',
    })
  }, [])

  // ブラウザを閉じる際に警告
  useEffect(() => {
    // autoreloadを害するのを防ぐ
    if (process.env.RAILS_ENV === 'development') {
      return
    }

    const handleBeforeUnloadEvent = (event: BeforeUnloadEvent): void => {
      event.returnValue = 'do not close'
    }
    window.addEventListener('beforeunload', handleBeforeUnloadEvent)
    return () => window.removeEventListener('beforeunload', handleBeforeUnloadEvent)
  }, [])

  return (
    <Fragment>
      <CssBaseline />
      <div css={wholeWrapperStyle}>
        <form
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
          encType="multipart/form-data"
        >
          <Box
            sx={{
              px: 2,
              boxSizing: 'border-box',
              height: '96px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Link href={routes.staff_admin_featured_article_path(article.id)}>« 特集に戻る</Link>
            <Divider orientation="vertical" sx={{ mx: 2 }}></Divider>
            <ComponentTemplateSelector
              componentTemplates={componentTemplates}
              insertTextWithCursor={insertTextWithCursor}
            />
            <Divider orientation="vertical" sx={{ mx: 2 }}></Divider>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained">
                保存
              </Button>
            </Box>
          </Box>
          <div css={mainWrapperStyle}>
            <div css={bodyWrapperStyle}>
              <textarea
                css={bodyStyle}
                {...restBodyRegister}
                ref={(e) => {
                  ref(e)
                  bodyRef.current = e // eslint-disable-line
                }}
                onDrop={handleBodyDrop}
              />
            </div>
            <PreviewComponent
              article={article}
              debouncedBody={debouncedBody}
              watchBody={watchBody}
              onError={onPreviewError}
            />
          </div>
        </form>
      </div>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarState({ ...snackbarState, open: false })
        }}
      >
        <Alert severity={snackbarState.severity} sx={{ width: '100%' }} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Fragment>
  )
}

const wholeWrapperStyle = css`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
`

const mainWrapperStyle = css`
  display: flex;
  width: 100%;
`

const bodyWrapperStyle = css`
  flex: 1;
`

const bodyStyle = css`
  width: 100%;
  min-height: calc(100vh - 96px);
  box-sizing: border-box;
  padding: 8px;
  margin: 0;
  font-size: 16px;
`

export default Show
